/*
Typed access to Matomo's `_paq` command queue.

`_paq` is a plain array until matomo.js has loaded (which only happens after
statistics consent, see TrackingCodes). Pushing into it before that is safe:
Matomo replays the queued commands in order once the tracker is up. If consent
is never given, the queue is simply discarded with the page.
*/

/**
 * Matomo tracker methods used in this codebase — single point of control for the
 * method names, including the ones the bootstrap script in TrackingCodes
 * interpolates into its inline JS.
 */
export const MATOMO_METHODS = {
  TRACK_PAGE_VIEW: 'trackPageView',
  TRACK_EVENT: 'trackEvent',
  SET_DOCUMENT_TITLE: 'setDocumentTitle',
  SET_CUSTOM_URL: 'setCustomUrl',
  SET_TRACKER_URL: 'setTrackerUrl',
  SET_SITE_ID: 'setSiteId',
  SET_CONSENT_GIVEN: 'setConsentGiven',
  SET_COOKIE_CONSENT_GIVEN: 'setCookieConsentGiven',
  FORGET_CONSENT_GIVEN: 'forgetConsentGiven',
  FORGET_COOKIE_CONSENT_GIVEN: 'forgetCookieConsentGiven',
  ENABLE_LINK_TRACKING: 'enableLinkTracking',
  TRACK_CONTENT_IMPRESSION: 'trackContentImpression',
  TRACK_CONTENT_INTERACTION: 'trackContentInteraction',
} as const;

export type MatomoMethod = (typeof MATOMO_METHODS)[keyof typeof MATOMO_METHODS];

/**
 * A single queued tracker call: the method name, followed by that method's
 * arguments in Matomo's order.
 *
 * - `method` — tracker method, see {@link MATOMO_METHODS}
 * - `args` — arguments of that method:
 *   - `trackEvent`: `category`, `action`, `name?`, `value?` (numeric)
 *   - `setDocumentTitle`: `title` — shown in the Pages report
 *   - `setCustomUrl`: absolute URL the following page view is counted for
 *   - `setSiteId`: Matomo site id, `setTrackerUrl`: matomo.php endpoint
 *   - `trackPageView`, `enableLinkTracking`, `*ConsentGiven`: no arguments
 *   - `trackContentImpression`: `contentName`, `contentPiece`, `contentTarget`
 *   - `trackContentInteraction`: `contentInteraction` FIRST, then `contentName`,
 *     `contentPiece`, `contentTarget` — the impression order does not apply here
 */
export type MatomoCommand = [
  method: MatomoMethod,
  ...args: (string | number | boolean)[],
];

type MatomoWindow = Window & {
  _paq?: MatomoCommand[];
  // Set by the Matomo bootstrap in TrackingCodes right after the initial page
  // view. Holds `location.href` at that moment, so components can tell "the
  // bootstrap already covered this URL" from "we navigated since then".
  // Note the href includes the hash — compare it via {@link getTrackedUrl} form.
  __hihMatomoInitialPageViewUrl?: string;
};

function getMatomoWindow(): MatomoWindow | null {
  return typeof window === 'undefined' ? null : (window as MatomoWindow);
}

/**
 * Queues Matomo commands. No-op during SSR.
 *
 * @param commands One {@link MatomoCommand} per tracker call — `[method, ...args]`,
 *   e.g. `[MATOMO_METHODS.SET_DOCUMENT_TITLE, 'Error 404']`,
 *   `[MATOMO_METHODS.SET_CUSTOM_URL, 'https://…/kaputt']`,
 *   `[MATOMO_METHODS.TRACK_PAGE_VIEW]` or
 *   `[MATOMO_METHODS.TRACK_EVENT, category, action, name, value]`.
 *   Order matters: title and URL must be queued before the page view they belong to.
 */
export function pushMatomo(...commands: MatomoCommand[]): void {
  const matomoWindow = getMatomoWindow();
  if (!matomoWindow) return;

  const paq = (matomoWindow._paq = matomoWindow._paq || []);
  commands.forEach((command) => paq.push(command));
}

/**
 * URL the Matomo bootstrap reported its initial page view for, or `undefined`
 * while the bootstrap has not run yet (statistics consent still pending).
 */
export function getInitialPageViewUrl(): string | undefined {
  return getMatomoWindow()?.__hihMatomoInitialPageViewUrl;
}

/**
 * The URL form page views are reported for: origin, path and query, without the
 * hash — Matomo does not evaluate it and it never reaches the report.
 *
 * Single source for the `setCustomUrl` argument and for the comparison in
 * {@link claimOwnPageView}, so "which URL was reported" cannot drift from "which
 * URL counts as covered". Client-side only, like every function here.
 */
export function getTrackedUrl(): string {
  return window.location.origin + window.location.pathname + window.location.search;
}

/**
 * Brings the bootstrap marker (`location.href`, hash included) into
 * {@link getTrackedUrl} form so both sides of the comparison are the same shape.
 */
function stripHash(url: string): string {
  const hashIndex = url.indexOf('#');

  return hashIndex === -1 ? url : url.slice(0, hashIndex);
}

/**
 * URL of the most recent page view a component claimed via {@link claimOwnPageView}.
 * `undefined` until the first client-side navigation, where the bootstrap's initial
 * URL stands in for it.
 *
 * The bootstrap's URL alone is not enough to compare against: it never changes, so
 * navigating back to the entry URL (`/` -> `/event/x` -> `/`) would look "already
 * covered" forever and that page view would be lost.
 */
let lastClaimedPageViewUrl: string | undefined;

/**
 * Claims the page view for the current URL: `true` when the caller has to send it
 * itself, and the URL is remembered so the same view cannot be claimed twice.
 *
 * `false` when
 * - statistics consent is still pending — nothing is sent yet, and the bootstrap
 *   will send one page view for whatever title and URL the `_paq` queue holds last.
 *   Pages left before the consent get no page view at all, see docs/tracking-consent.md.
 * - the current URL is already covered, either by the bootstrap on a server load or
 *   by an earlier claim (effect re-run, StrictMode double-invoke).
 *
 * Side-effecting on purpose: call it exactly once per page view, directly in front of
 * the `trackPageView` push it guards.
 */
export function claimOwnPageView(): boolean {
  const initialPageViewUrl = getInitialPageViewUrl();
  if (initialPageViewUrl === undefined) return false;

  const currentUrl = getTrackedUrl();
  const coveredUrl = lastClaimedPageViewUrl ?? stripHash(initialPageViewUrl);
  if (coveredUrl === currentUrl) return false;

  lastClaimedPageViewUrl = currentUrl;

  return true;
}

/*
Event taxonomy (Matomo "Ereignisse"-Report). Unlike the tracker method names
above these are OUR values — analytics can ask for a rename at any time — so each
one exists exactly once and the reports stay greppable from here.
*/

/**
 * Page titles as they appear in the Matomo reports. A page lands here once it has
 * content tracking, because its title is then needed twice: as the page view's
 * `customTitle` and as the first half of every content name on it (see
 * {@link buildContentName}). Pages without content tracking still pass their title
 * inline to MatomoPageView.
 */
export const MATOMO_PAGE_TITLES = {
  TODAY: 'Heute',
  CALENDAR: 'Kalender',
  ALWAYS: 'Geht-Immer',
  EVENT: 'Event',
  GUIDE: 'Guide',
  CATEGORY: 'Kategorie',
} as const;

export const MATOMO_CATEGORIES = {
  INTERACTIONS: 'Interaktionen',
  ERROR: 'Fehler',
} as const;

export const MATOMO_ACTIONS = {
  TICKET_CLICK: 'Ticket Klick',
  LOCATION_CLICK: 'Location Klick',
  EVENT_TIP_CLICK: 'Event Tipp Klick',
  ANNOUNCEMENT_CLICK: 'Announcement Klick',
  BOOKMARK_CLICK: 'Gespeichert Klick',
  WEEK_CHANGE: 'Wochenwechsel',
  DAY_CHANGE: 'Tageswechsel',
  ERROR_PAGE_CLICK: 'Error Page Klick',
  ERROR_STATE: 'Error State',
} as const;

/**
 * Event names. The `*_PREFIX` entries are completed at the call site with dynamic
 * data (bookmark type, announcement URL).
 */
export const MATOMO_NAMES = {
  EVENT_LOCATION_CLICK: 'Event Location Klick',
  CALENDAR_TICKET_CLICK: 'Kalender Ticket Klick',
  CALENDAR_LOCATION_CLICK: 'Kalender Location Klick',
  CALENDAR_EVENT_TIP_CLICK: 'Kalender Event Tipp Klick',
  CALENDAR_WEEK_CHANGE: 'Kalender Wochenwechsel',
  CALENDAR_DAY_CHANGE: 'Kalender Tageswechsel',
  TODAY_TICKET_CLICK: 'Heute Ticket Klick',
  TODAY_LOCATION_CLICK: 'Heute Location Klick',
  TODAY_RECOMMENDATION_CLICK: 'Heute Recommendation Klick',
  TODAY_ANNOUNCEMENT_CLICK_PREFIX: 'Heute Announcement Klick',
  BOOKMARK_PREFIX: 'Bookmark',
  BOOKMARK_ADD_SUFFIX: 'Speichern Klick',
  BOOKMARK_REMOVE_SUFFIX: 'Entfernen Klick',
  HOME_CLICK: 'Zur Startseite Klick',
} as const;

/**
 * Queues a Matomo event. Page views are NOT tracked this way, but rather as Page
 * Views (see ErrorPageTracking) — event tracking is reserved for interactions and
 * error states. `value` requires a `name`, otherwise Matomo reads it as the name.
 */
export function trackMatomoEvent(
  category: string,
  action: string,
  name?: string,
  value?: number,
): void {
  pushMatomo([
    MATOMO_METHODS.TRACK_EVENT,
    category,
    action,
    ...(name !== undefined ? [name] : []),
    ...(value !== undefined ? [value] : []),
  ]);
}

/** Click "Zur Startseite" on an error page (404/410). */
export function trackErrorPageHomeClick(): void {
  trackMatomoEvent(
    MATOMO_CATEGORIES.INTERACTIONS,
    MATOMO_ACTIONS.ERROR_PAGE_CLICK,
    MATOMO_NAMES.HOME_CLICK,
  );
}

/**
 * Error state displayed within a page (no error page is called),
 * e.g., if the saved content could not be loaded.
 */
export function trackErrorStateShown(context: string): void {
  trackMatomoEvent(MATOMO_CATEGORIES.ERROR, MATOMO_ACTIONS.ERROR_STATE, context);
}

/*
Content tracking (Matomo "Inhalte"-Report). Feeds a separate report from the events
above: an impression per card that was actually seen, an interaction per click on
it — together they give the interaction rate per content piece.

The values are OURS, same as the event taxonomy. Schema agreed with analytics
(HIH-1236):

  content name   `<page title> - <module type>`   Heute - ARTICLE_CONTENT_MODULE
  content piece  `<kind> - <slug>`                Guide - hamburgs-beste-cafes
  content target path of the detail page          /guide/hamburgs-beste-cafes
  interaction    see MATOMO_CONTENT_INTERACTIONS  Geöffnet

Impressions are triggered by an IntersectionObserver (see useContentImpression), not
by Matomo's own DOM scan: its visibility check has no threshold — a single visible
pixel counts — and its automatic interaction tracking attaches its own click handlers
to links.
*/

export const MATOMO_CONTENT_INTERACTIONS = {
  DETAILSEITE_GEOEFFNET: 'Detailseite geöffnet',
  BOOKMARK: 'Gespeichert',
} as const;

/** Content piece prefixes — same vocabulary as the page view titles of the detail pages. */
export const MATOMO_CONTENT_KINDS = {
  EVENT: 'Event',
  GUIDE: 'Guide',
} as const;

export type MatomoContentKind =
  (typeof MATOMO_CONTENT_KINDS)[keyof typeof MATOMO_CONTENT_KINDS];

/** The three values that identify one tracked card. */
export type MatomoContentBlock = {
  /** Where the card sits, see {@link buildContentName}. */
  contentName: string;
  /** Which content it is, see {@link buildContentPiece}. */
  contentPiece: string;
  /** Path the card leads to, e.g. `/event/<slug>`. */
  contentTarget: string;
};

/** `Heute - ARTICLE_CONTENT_MODULE` — the place a card is shown in. */
export function buildContentName(pageTitle: string, moduleType: string): string {
  return `${pageTitle} - ${moduleType}`;
}

/** `Event - <slug>` / `Guide - <slug>` — the content itself. */
export function buildContentPiece(kind: MatomoContentKind, slug: string): string {
  return `${kind} - ${slug}`;
}

/**
 * Content blocks that already reported an impression, together with the URL they
 * were counted for — a page view is the unit an impression is counted per.
 *
 * Keyed by the tracked values, not by the DOM node: the same card exists more than
 * once in the DOM (Swiper clones looped slides, and the sliders render a desktop and
 * a mobile variant of the same list), and all of those must count once.
 */
let claimedImpressionsUrl: string | undefined;
let claimedImpressionKeys = new Set<string>();

function getContentBlockKey({
  contentName,
  contentPiece,
  contentTarget,
}: MatomoContentBlock): string {
  return `${contentName}|${contentPiece}|${contentTarget}`;
}

/**
 * Claims the impression of a content block for the current URL: `true` when the
 * caller has to send it, `false` when this block was already counted here.
 *
 * Side-effecting on purpose, like {@link claimOwnPageView}: call it directly in front
 * of the {@link trackContentImpression} it guards.
 */
export function claimContentImpression(block: MatomoContentBlock): boolean {
  const currentUrl = getTrackedUrl();

  if (claimedImpressionsUrl !== currentUrl) {
    claimedImpressionsUrl = currentUrl;
    claimedImpressionKeys = new Set();
  }

  const key = getContentBlockKey(block);
  if (claimedImpressionKeys.has(key)) return false;

  claimedImpressionKeys.add(key);

  return true;
}

/** Queues the impression of a card. Guard it with {@link claimContentImpression}. */
export function trackContentImpression({
  contentName,
  contentPiece,
  contentTarget,
}: MatomoContentBlock): void {
  pushMatomo([
    MATOMO_METHODS.TRACK_CONTENT_IMPRESSION,
    contentName,
    contentPiece,
    contentTarget,
  ]);
}

/**
 * Queues an interaction with a card, e.g. `MATOMO_CONTENT_INTERACTIONS.DETAILSEITE_GEOEFFNET`.
 * Not guarded: every interaction counts, also a repeated one.
 */
export function trackContentInteraction(
  interaction: string,
  { contentName, contentPiece, contentTarget }: MatomoContentBlock,
): void {
  pushMatomo([
    MATOMO_METHODS.TRACK_CONTENT_INTERACTION,
    interaction,
    contentName,
    contentPiece,
    contentTarget,
  ]);
}
